import * as Sentry from "@sentry/react";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { PERSONA_AI_EDIT_IDS_MAX } from "@nexus/contracts";

import {
  personaOpportunitySetBriefsQueryOptions,
  personaOpportunitySetScriptsQueryOptions,
  useAiEditPersonaBriefs,
  useAiEditPersonaScripts,
  useDeletePersonaBriefs,
  useDeletePersonaScripts,
} from "@/lib/queries";
import { useIsActiveSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import type { SentScriptsMap } from "@/features/marketplace/types/marketplace.types";

import { formatPersonaBriefForCopy } from "../../../lib/persona-brief-clipboard.js";
import {
  downloadPersonaSelection,
  personaSelectionFilename,
  scriptDocxLabels,
  type PersonaDocxPart,
  type PersonaDocumentExportFormat,
} from "../../../lib/persona-script-docx.js";
import { DocumentExportMenu } from "../../shared/DocumentExportMenu/DocumentExportMenu.js";
import { BroadcastScriptsModal } from "../../workspace/tabs/ScriptsTab/BroadcastScriptsModal.js";
import { DeletePersonaScriptsDialog } from "../../workspace/tabs/ScriptsTab/DeletePersonaScriptsDialog.js";
import { PersonaAiBriefEditorModal } from "../../workspace/tabs/ScriptsTab/PersonaAiBriefEditorModal.js";
import { PersonaAiScriptEditorModal } from "../../workspace/tabs/ScriptsTab/PersonaAiScriptEditorModal.js";
import { PersonaSendToVendorDialog } from "../../workspace/tabs/ScriptsTab/PersonaSendToVendorDialog/PersonaSendToVendorDialog.js";
import { OpportunityHubActionBar, type HubBarAction } from "../PersonaOpportunityHubChrome/OpportunityHubActionBar.js";
import { groupBySet, maxGroupSize, splitByKind } from "../PersonaOpportunityHubChrome/hub-fanout.js";
import type { HubChromeScriptRow } from "../PersonaOpportunityHubChrome/hub-chrome.types.js";

export interface OpportunityHubScriptActionsProps {
  tenantId: string | null;
  rows: HubChromeScriptRow[];
  selectedIds: string[];
  canEdit: boolean;
  sentScripts: SentScriptsMap;
  onClearSelection: () => void;
}

/** Selection fly-in for the hub Scripts & Briefs tab. */
/** Set of the first script the user selected (selection order), else of the first listed script. */
function firstSelectedScriptSetName(
  scripts: readonly HubChromeScriptRow[],
  selectedIds: readonly string[],
): string | undefined {
  const byId = new Map(scripts.map((row) => [row.id, row]));
  const first = selectedIds.map((id) => byId.get(id)).find((row) => row !== undefined) ?? scripts[0];
  return first?.setName;
}

export function OpportunityHubScriptActions({
  tenantId,
  rows,
  selectedIds,
  canEdit,
  sentScripts,
  onClearSelection,
}: OpportunityHubScriptActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isActiveSuperTenant = useIsActiveSuperTenant();
  const perms = usePermissions();
  // The role on the active tenant must hold the grant; no super-tenant bypass (AD-3319).
  const canBroadcast = isActiveSuperTenant && perms.SCRIPTS_BROADCAST.isOn;

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isAiEditScriptsOpen, setIsAiEditScriptsOpen] = React.useState(false);
  const [isAiEditBriefsOpen, setIsAiEditBriefsOpen] = React.useState(false);
  const [isSendOpen, setIsSendOpen] = React.useState(false);
  const [isBroadcastOpen, setIsBroadcastOpen] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  const deleteScripts = useDeletePersonaScripts();
  const deleteBriefs = useDeletePersonaBriefs();
  const aiEditScripts = useAiEditPersonaScripts();
  const aiEditBriefs = useAiEditPersonaBriefs();

  const { scripts, briefs } = React.useMemo(
    () => splitByKind(rows, selectedIds),
    [rows, selectedIds],
  );
  const scriptGroups = React.useMemo(
    () => groupBySet(scripts, scripts.map((row) => row.id)),
    [scripts],
  );
  const briefGroups = React.useMemo(
    () => groupBySet(briefs, briefs.map((row) => row.id)),
    [briefs],
  );

  const selectedCount = scripts.length + briefs.length;
  const isDeleting = deleteScripts.isPending || deleteBriefs.isPending;
  const isAiEditing = aiEditScripts.isPending || aiEditBriefs.isPending;

  // The AI Edit cap is enforced per job, and the hub fans out one job per set.
  const scriptsOverCap = maxGroupSize(scriptGroups) > PERSONA_AI_EDIT_IDS_MAX;
  const briefsOverCap = maxGroupSize(briefGroups) > PERSONA_AI_EDIT_IDS_MAX;
  const overCapWarning =
    scriptsOverCap || briefsOverCap
      ? t("personaOpportunities.hub.bulk.aiEditLimit", {
          max: PERSONA_AI_EDIT_IDS_MAX,
          defaultValue:
            "AI Edit runs one job per set and is limited to {{max}} items per set. Deselect some to continue.",
        })
      : null;

  const titlesById = (kind: "script" | "brief"): Record<string, string> =>
    Object.fromEntries(
      (kind === "script" ? scripts : briefs).map((row) => [row.id, row.title]),
    );

  async function handleExport(format: PersonaDocumentExportFormat) {
    if (!tenantId || selectedCount === 0) return;
    setIsExporting(true);
    try {
      // Hub rows carry no body, so the full documents are fetched per set first.
      const [scriptDocs, briefDocs] = await Promise.all([
        Promise.all(
          scriptGroups.map((group) =>
            queryClient.ensureQueryData(
              personaOpportunitySetScriptsQueryOptions(tenantId, group.setId),
            ),
          ),
        ),
        Promise.all(
          briefGroups.map((group) =>
            queryClient.ensureQueryData(
              personaOpportunitySetBriefsQueryOptions(tenantId, group.setId),
            ),
          ),
        ),
      ]);

      const selectedScriptIds = new Set(scripts.map((row) => row.id));
      const selectedBriefIds = new Set(briefs.map((row) => row.id));
      const labels = scriptDocxLabels(t);
      const parts: PersonaDocxPart[] = [];
      const foundScriptIds = new Set<string>();
      const foundBriefIds = new Set<string>();

      for (const script of scriptDocs.flat()) {
        if (!selectedScriptIds.has(script.id) || foundScriptIds.has(script.id)) continue;
        foundScriptIds.add(script.id);
        parts.push({
          kind: "script",
          script,
          untitled: t("personaOpportunities.scripts.untitledHook", {
            defaultValue: "Script {{slot}}",
            slot: script.slotIndex,
          }),
          labels,
        });
      }
      for (const brief of briefDocs.flat()) {
        if (!selectedBriefIds.has(brief.id) || foundBriefIds.has(brief.id)) continue;
        foundBriefIds.add(brief.id);
        parts.push({ kind: "brief", text: formatPersonaBriefForCopy(brief, t), labels });
      }

      const missingScript = scripts.some((row) => !foundScriptIds.has(row.id));
      const missingBrief = briefs.some((row) => !foundBriefIds.has(row.id));
      if (missingScript || missingBrief) {
        throw new Error("Selected documents were not all available to export");
      }

      await downloadPersonaSelection(
        parts,
        format,
        personaSelectionFilename(scripts.length, briefs.length, format),
      );

      toast.success(
        t("personaOpportunities.hub.bulk.exportToast", {
          count: parts.length,
          defaultValue_one: "{{count}} document exported",
          defaultValue_other: "{{count}} documents exported",
        }),
      );
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: "persona-hub-script-export" } });
      toast.error(
        t("personaOpportunities.hub.bulk.exportFailed", "Could not export the selected documents"),
      );
    } finally {
      setIsExporting(false);
    }
  }

  async function handleDeleteConfirmed() {
    if (!tenantId) return;
    // Delete silently skips ids from another set, so each set needs its own call.
    await Promise.allSettled([
      ...scriptGroups.map((group) =>
        deleteScripts.mutateAsync({ setId: group.setId, tenantId, scriptIds: group.ids }),
      ),
      ...briefGroups.map((group) =>
        deleteBriefs.mutateAsync({ setId: group.setId, tenantId, briefIds: group.ids }),
      ),
    ]);
    setIsDeleteOpen(false);
    onClearSelection();
  }

  function handleAiEditScripts(instruction: string) {
    if (!tenantId) return;
    for (const group of scriptGroups) {
      aiEditScripts.mutate({
        setId: group.setId,
        tenantId,
        scriptIds: group.ids,
        instruction,
      });
    }
    setIsAiEditScriptsOpen(false);
    onClearSelection();
  }

  function handleAiEditBriefs(instruction: string, idempotencyKey: string) {
    if (!tenantId) return;
    for (const group of briefGroups) {
      aiEditBriefs.mutate({
        setId: group.setId,
        tenantId,
        briefIds: group.ids,
        instruction,
        idempotencyKey,
      });
    }
    setIsAiEditBriefsOpen(false);
    onClearSelection();
  }

  const actions: HubBarAction[] = [];
  if (canEdit) {
    actions.push({
      id: "delete",
      label: t("personaOpportunities.scripts.bulk.delete", "Delete"),
      icon: "trash-2",
      variant: "destructive",
      disabled: isDeleting,
      testId: "opportunity-hub-script-delete",
      onClick: () => setIsDeleteOpen(true),
    });
  }
  actions.push({
    kind: "custom",
    id: "export",
    node: (
      <DocumentExportMenu
        busy={isExporting}
        testId="opportunity-hub-script-export"
        className="w-full text-xs sm:w-auto"
        onExport={(format) => void handleExport(format)}
      />
    ),
  });
  if (canEdit && scripts.length > 0) {
    actions.push({
      id: "ai-edit-scripts",
      label:
        briefs.length > 0
          ? t("personaOpportunities.scripts.bulk.aiEditScripts", "AI Edit scripts")
          : t("personaOpportunities.scripts.actions.aiEdit", "AI Edit"),
      icon: "sparkles",
      variant: "ai",
      className: "border border-ai",
      busy: isAiEditing,
      disabled: scriptsOverCap,
      ...(scriptsOverCap && overCapWarning ? { tooltip: overCapWarning } : {}),
      testId: "opportunity-hub-script-ai-edit",
      onClick: () => setIsAiEditScriptsOpen(true),
    });
  }
  if (canEdit && briefs.length > 0) {
    actions.push({
      id: "ai-edit-briefs",
      label:
        scripts.length > 0
          ? t("personaOpportunities.scripts.briefs.bulk.aiEditBriefs", "AI Edit briefs")
          : t("personaOpportunities.scripts.actions.aiEdit", "AI Edit"),
      icon: "sparkles",
      variant: "ai",
      className: "border border-ai",
      busy: isAiEditing,
      disabled: briefsOverCap,
      ...(briefsOverCap && overCapWarning ? { tooltip: overCapWarning } : {}),
      testId: "opportunity-hub-brief-ai-edit",
      onClick: () => setIsAiEditBriefsOpen(true),
    });
  }
  // Super tenant whose role holds the grant: the selected scripts are sources for other tenants.
  if (canBroadcast && scripts.length > 0 && briefs.length === 0) {
    actions.push({
      id: "broadcast",
      label: t("personaOpportunities.scripts.bulk.broadcast", "Generate for tenants"),
      variant: "secondary",
      testId: "opportunity-hub-script-broadcast",
      onClick: () => setIsBroadcastOpen(true),
    });
  }
  actions.push({
    id: "send-to-vendor",
    label: t("personaOpportunities.scripts.actions.sendToVendor", "Send to production partner"),
    testId: "opportunity-hub-script-send",
    onClick: () => setIsSendOpen(true),
  });

  const primaryScript = scripts[0] ?? null;
  const primaryBrief = briefs[0] ?? null;

  return (
    <>
      <OpportunityHubActionBar
        selectedCount={selectedCount}
        selectedLabel={t("personaOpportunities.hub.bulk.documentsSelected", {
          count: selectedCount,
          defaultValue_one: "{{count}} document selected",
          defaultValue_other: "{{count}} documents selected",
        })}
        onClearSelection={onClearSelection}
        ariaLabel={t("personaOpportunities.hub.bulk.documentsBarAria", {
          count: selectedCount,
          defaultValue_one: "Bulk actions for {{count}} selected document",
          defaultValue_other: "Bulk actions for {{count}} selected documents",
        })}
        warning={overCapWarning}
        testId="opportunity-hub-script-bar"
        actions={actions}
      />

      <DeletePersonaScriptsDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        selectedScriptCount={scripts.length}
        selectedBriefCount={briefs.length}
        isDeleting={isDeleting}
        onConfirm={() => void handleDeleteConfirmed()}
      />

      {isAiEditScriptsOpen && primaryScript ? (
        <PersonaAiScriptEditorModal
          open
          hook={null}
          concept={null}
          opportunityTitle={primaryScript.opportunityAngle}
          isSubmitting={isAiEditing}
          onClose={() => setIsAiEditScriptsOpen(false)}
          onSubmit={handleAiEditScripts}
        />
      ) : null}

      {isAiEditBriefsOpen && primaryBrief ? (
        <PersonaAiBriefEditorModal
          open
          videoName={primaryBrief.title}
          opportunityTitle={primaryBrief.opportunityAngle}
          count={briefs.length}
          isSubmitting={isAiEditing}
          onClose={() => setIsAiEditBriefsOpen(false)}
          onSubmit={handleAiEditBriefs}
        />
      ) : null}

      {isBroadcastOpen && scripts.length > 0 ? (
        <BroadcastScriptsModal
          open
          onOpenChange={setIsBroadcastOpen}
          scripts={scripts.map((row) => ({ id: row.id, title: row.title }))}
          sourceSetName={firstSelectedScriptSetName(scripts, selectedIds)}
          onStarted={onClearSelection}
        />
      ) : null}

      {isSendOpen && tenantId ? (
        <PersonaSendToVendorDialog
          open
          onOpenChange={setIsSendOpen}
          scriptIds={scripts.map((row) => row.id)}
          briefIds={briefs.map((row) => row.id)}
          scriptTitlesById={titlesById("script")}
          briefTitlesById={titlesById("brief")}
          sentScriptsMap={sentScripts}
          tenantId={tenantId}
          // Orders are tenant-scoped; this only seeds the "view order" deep link.
          opportunitySetId={primaryScript?.setId ?? primaryBrief?.setId ?? ""}
        />
      ) : null}
    </>
  );
}

